import { type RefObject, useLayoutEffect, useState } from "react";

export type ListTableRowActionsPlacement = "top-start" | "bottom-start";

const PANEL_HEIGHT_ESTIMATE_PX = 360;
const EDGE_PAD_PX = 16;

export function useListTableRowActionsPlacement(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  boundaryRef: RefObject<HTMLElement | null> | null,
  panelHeightEstimatePx: number = PANEL_HEIGHT_ESTIMATE_PX
): ListTableRowActionsPlacement {
  const [placement, setPlacement] = useState<ListTableRowActionsPlacement>("bottom-start");

  useLayoutEffect(() => {
    if (!open) return;

    const measure = (): void => {
      const el = anchorRef.current;
      if (!el) return;

      const t = el.getBoundingClientRect();
      let spaceBelow: number;
      let spaceAbove: number;

      const root = boundaryRef?.current;
      if (root) {
        const r = root.getBoundingClientRect();
        spaceBelow = Math.max(0, r.bottom - t.bottom - EDGE_PAD_PX);
        spaceAbove = Math.max(0, t.top - r.top - EDGE_PAD_PX);
      } else {
        spaceBelow = Math.max(0, window.innerHeight - t.bottom - EDGE_PAD_PX);
        spaceAbove = Math.max(0, t.top - EDGE_PAD_PX);
      }

      const H = Math.max(96, panelHeightEstimatePx);
      let next: ListTableRowActionsPlacement;
      if (spaceBelow >= H && spaceAbove >= H) {
        next = spaceBelow >= spaceAbove ? "bottom-start" : "top-start";
      } else if (spaceBelow >= H) {
        next = "bottom-start";
      } else if (spaceAbove >= H) {
        next = "top-start";
      } else {
        next = spaceBelow >= spaceAbove ? "bottom-start" : "top-start";
      }

      setPlacement(next);
    };

    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [open, anchorRef, boundaryRef, panelHeightEstimatePx]);

  return placement;
}
